/**
 * A tiny procedural synthwave engine (Web Audio). This is the *sonification* of the beat grid —
 * cosmetic only, per Design Bible §5. Gameplay never reads it, and a run with the synth muted or
 * unavailable is identical (Pillar P6).
 *
 * We can't ship licensed tracks (FS-18 K3), so Sector music is generated: a kick on every beat, an
 * off-beat bass following a chord progression, and a shimmering arp. Overdrive lifts a high band in.
 */
type Ctx = AudioContext;

const MINOR_PENTA = [0, 3, 5, 7, 10];
/** Layers the procedural bed can add, for `stem-add` (FS-01 §5.3). Kick is layer 0 and is always on. */
export const MAX_STEMS = 4;

export class Synth {
  private ctx: Ctx | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private liftGain: GainNode | null = null; // Overdrive "lift" stem.
  private sfxGain: GainNode | null = null;

  private bpm = 128;
  private running = false;
  private nextNoteTime = 0;
  private beatIndex = 0;
  /** Active music layers. Full outside onboarding; onboarding starts at 1 and earns the rest. */
  private stems = MAX_STEMS;
  private timer = 0;
  private rootHz = 55; // A1
  private progression = [0, 0, 5, 7]; // bars: i i iv v (semitone offsets from root)
  private enabled = true;

  // Custom Track (FS-09 R26) — the user's own decoded audio, played back verbatim alongside the
  // procedural hazard scheduling. Mutually exclusive with `startMusic`'s generated bed.
  private trackSource: AudioBufferSourceNode | null = null;
  private trackGain: GainNode | null = null;

  get isUnlocked(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** For Custom Track's `decodeAudioData`/analysis — null until `init()`/`unlock()` has run. */
  get audioContext(): AudioContext | null {
    return this.ctx;
  }

  /** Constructed suspended (no user gesture needed to build it). */
  init(): void {
    if (this.ctx || !this.enabled) return;
    try {
      const AC: typeof AudioContext =
        window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) {
        this.enabled = false;
        return;
      }
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      this.master.connect(this.ctx.destination);

      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.55;
      this.musicGain.connect(this.master);

      this.liftGain = this.ctx.createGain();
      this.liftGain.gain.value = 0.0;
      this.liftGain.connect(this.master);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = 0.7;
      this.sfxGain.connect(this.master);
    } catch {
      this.enabled = false;
      this.ctx = null;
    }
  }

  /** Resume on a user gesture. Returns the context state. */
  async unlock(): Promise<string> {
    if (!this.ctx) this.init();
    if (!this.ctx) return 'unavailable';
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        /* ignore */
      }
    }
    return this.ctx.state;
  }

  startMusic(bpm: number): void {
    if (!this.ctx || !this.enabled) return;
    this.bpm = bpm;
    this.beatIndex = 0;
    this.nextNoteTime = this.ctx.currentTime + 0.08;
    if (!this.running) {
      this.running = true;
      this.scheduleLoop();
    }
  }

  stopMusic(): void {
    this.running = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = 0;
    }
    if (this.liftGain && this.ctx) this.liftGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
  }

  /** Play the user's decoded track verbatim (FS-09 R26) — the procedural bed stays silent alongside it. */
  playCustomTrack(buffer: AudioBuffer): void {
    if (!this.ctx || !this.master) return;
    this.stopCustomTrack();
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const g = this.ctx.createGain();
    g.gain.value = 0.8;
    src.connect(g).connect(this.master);
    src.start();
    this.trackSource = src;
    this.trackGain = g;
  }

  stopCustomTrack(): void {
    if (this.trackSource) {
      try {
        this.trackSource.stop();
      } catch {
        /* already stopped */
      }
      this.trackSource.disconnect();
      this.trackSource = null;
    }
    if (this.trackGain) {
      this.trackGain.disconnect();
      this.trackGain = null;
    }
  }

  /** Pause suspends the whole `AudioContext` (FS-09 R16) — music, lift, SFX and the custom track alike. */
  suspend(): void {
    if (this.ctx && this.ctx.state === 'running') void this.ctx.suspend();
  }

  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /**
   * Scale the music bed 0..1 (The Silent Node fades to true silence). Gameplay is unaffected — the
   * AudioClock is sim-driven, so a silenced boss plays identically to a heard one (Pillar P6).
   */
  setMusicLevel(level: number): void {
    if (!this.ctx || !this.musicGain) return;
    const target = 0.55 * Math.max(0, Math.min(1, level));
    if (Math.abs(this.musicGain.gain.value - target) < 0.01) return;
    this.musicGain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.25);
  }

  /**
   * `stem-add` (FS-01 §5.3) — how many music layers are playing, 0..`MAX_STEMS`. Onboarding starts
   * at one and adds a layer on each successful teach, which is why the music is also the teacher.
   *
   * §5.3 requires every stem-add to have a **visual equivalent** (a HUD reveal or an emissive step)
   * so a muted player loses no information; that half lives at the call site in `main.ts`. Outside
   * onboarding this stays at `MAX_STEMS` and the track is unchanged.
   */
  setStems(n: number): void {
    this.stems = Math.max(0, Math.min(MAX_STEMS, Math.floor(n)));
  }

  get stemCount(): number {
    return this.stems;
  }

  /**
   * The filter sweep that announces onboarding's phantom → lethal transition (§5.2 bar 9.1). The
   * visual half — hollow outline becoming solid fill — is the requirement (R08); this is the
   * flourish on top of it, and muted play loses nothing.
   */
  filterSweep(): void {
    if (!this.ctx || !this.musicGain) return;
    const t = this.ctx.currentTime;
    this.musicGain.gain.setValueAtTime(this.musicGain.gain.value, t);
    this.musicGain.gain.linearRampToValueAtTime(0.18, t + 0.12);
    this.musicGain.gain.setTargetAtTime(0.55, t + 0.16, 0.35);
  }

  setOverdrive(on: boolean): void {
    if (!this.ctx || !this.liftGain || !this.musicGain) return;
    const t = this.ctx.currentTime;
    this.liftGain.gain.setTargetAtTime(on ? 0.5 : 0.0, t, 0.15);
    this.musicGain.gain.setTargetAtTime(on ? 0.4 : 0.55, t, 0.15);
  }

  private scheduleLoop = (): void => {
    if (!this.running || !this.ctx) return;
    const secondsPerBeat = 60 / this.bpm;
    // Look-ahead scheduler.
    while (this.nextNoteTime < this.ctx.currentTime + 0.12) {
      this.scheduleBeat(this.beatIndex, this.nextNoteTime, secondsPerBeat);
      this.beatIndex++;
      this.nextNoteTime += secondsPerBeat;
    }
    this.timer = window.setTimeout(this.scheduleLoop, 25);
  };

  private scheduleBeat(beat: number, time: number, spb: number): void {
    const bar = Math.floor(beat / 4) % this.progression.length;
    const chordRoot = this.rootHz * Math.pow(2, this.progression[bar]! / 12);
    const stems = this.stems;

    // Kick every beat — stem 0, the layer that is always there.
    this.kick(time);
    // Bass on beats, with an off-beat eighth for drive.
    if (stems >= 1) this.bass(chordRoot, time, spb * 0.9);
    if (stems >= 3) this.bass(chordRoot * 1.0, time + spb * 0.5, spb * 0.4);
    // Arp: a pentatonic note per beat, an octave and a fifth up.
    const step = MINOR_PENTA[beat % MINOR_PENTA.length]!;
    const arpHz = chordRoot * 4 * Math.pow(2, step / 12);
    if (stems >= 2) this.arp(arpHz, time, spb * 0.45);
    if (stems >= 4 && beat % 2 === 1) this.arp(arpHz * 1.5, time + spb * 0.5, spb * 0.3);
  }

  private kick(time: number): void {
    if (!this.ctx || !this.musicGain) return;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.frequency.setValueAtTime(150, time);
    osc.frequency.exponentialRampToValueAtTime(45, time + 0.12);
    g.gain.setValueAtTime(0.9, time);
    g.gain.exponentialRampToValueAtTime(0.001, time + 0.16);
    osc.connect(g).connect(this.musicGain);
    osc.start(time);
    osc.stop(time + 0.18);
  }

  private bass(hz: number, time: number, dur: number): void {
    if (!this.ctx || !this.musicGain) return;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.value = hz;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = 420;
    g.gain.setValueAtTime(0.0, time);
    g.gain.linearRampToValueAtTime(0.25, time + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, time + dur);
    osc.connect(filt).connect(g).connect(this.musicGain);
    osc.start(time);
    osc.stop(time + dur + 0.02);
  }

  private arp(hz: number, time: number, dur: number): void {
    if (!this.ctx || !this.musicGain || !this.liftGain) return;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = hz;
    g.gain.setValueAtTime(0.0, time);
    g.gain.linearRampToValueAtTime(0.12, time + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, time + dur);
    osc.connect(g).connect(this.musicGain);
    // Also feed the lift band (brighter octave) — audible only in Overdrive.
    const lift = this.ctx.createOscillator();
    const lg = this.ctx.createGain();
    lift.type = 'sawtooth';
    lift.frequency.value = hz * 2;
    lg.gain.setValueAtTime(0.0, time);
    lg.gain.linearRampToValueAtTime(0.09, time + 0.005);
    lg.gain.exponentialRampToValueAtTime(0.001, time + dur);
    lift.connect(lg).connect(this.liftGain);
    osc.start(time);
    osc.stop(time + dur + 0.02);
    lift.start(time);
    lift.stop(time + dur + 0.02);
  }

  // ── One-shot SFX (Design Bible §12: every event also has a non-audio channel in the HUD) ──
  sfx(name: 'graze' | 'node' | 'hit' | 'death' | 'overdrive' | 'pickup' | 'miss' | 'tick'): void {
    if (!this.ctx || !this.sfxGain || this.ctx.state !== 'running') return;
    const t = this.ctx.currentTime;
    switch (name) {
      case 'graze':
        this.blip(1760, 0.06, t, 'sine', 0.18);
        break;
      case 'node':
        this.blip(1320, 0.12, t, 'triangle', 0.22);
        this.blip(1980, 0.1, t + 0.02, 'sine', 0.14);
        break;
      case 'tick':
        this.blip(2640, 0.03, t, 'square', 0.06);
        break;
      case 'pickup':
        this.blip(660, 0.08, t, 'triangle', 0.25);
        this.blip(990, 0.12, t + 0.06, 'triangle', 0.22);
        break;
      // FS-07 R38 — the miss cue is the pickup cue inverted: same timbre so it is recognisably
      // *that object*, falling instead of rising so it reads as a loss. A player who dodged a
      // pickup has to learn it was collectable, and silence teaches nothing.
      case 'miss':
        this.blip(560, 0.10, t, 'triangle', 0.16);
        this.blip(370, 0.16, t + 0.07, 'triangle', 0.13);
        break;
      case 'overdrive':
        this.blip(440, 0.5, t, 'sawtooth', 0.3);
        this.blip(880, 0.5, t + 0.05, 'sawtooth', 0.22);
        break;
      case 'hit':
        this.noise(0.25, t, 900);
        break;
      case 'death':
        this.noise(0.8, t, 300);
        this.blip(110, 0.7, t, 'sawtooth', 0.3);
        break;
    }
  }

  private blip(hz: number, dur: number, time: number, type: OscillatorType, gain: number): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = hz;
    g.gain.setValueAtTime(gain, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    osc.connect(g).connect(this.sfxGain);
    osc.start(time);
    osc.stop(time + dur + 0.02);
  }

  private noise(dur: number, time: number, cutoff: number): void {
    if (!this.ctx || !this.sfxGain) return;
    const frames = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, frames, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.value = cutoff;
    const g = this.ctx.createGain();
    g.gain.value = 0.5;
    src.connect(filt).connect(g).connect(this.sfxGain);
    src.start(time);
  }
}
